/**
 * WhatsApp: el telefono argentino y el link al chat.
 *
 * Vive en `@tora/shared` porque lo usan los dos lados: el frontend, cuando el
 * panel de venta cerrada pide el numero y lo muestra antes de abrir el chat; y
 * el backend, cuando `/r/:token/whatsapp` redirige al chat del cliente con el
 * telefono de su ficha. Si la heuristica del 15 viviera dos veces, un dia
 * abririan chats distintos para el mismo numero.
 */

export interface TelefonoNormalizado {
  /** Listo para `wa.me`: solo digitos, con 54 y el 9. */
  e164: string;
  /** Como se lee, para que la persona lo confirme: +54 9 376 412-3456. */
  legible: string;
  /**
   * `alta` cuando quedaron exactamente los 10 digitos de area + numero.
   * `baja` cuando no: se manda igual, pero la pantalla avisa.
   */
  confianza: "alta" | "baja";
}

/**
 * Lleva lo que sea que hayan tipeado al formato que `wa.me` exige.
 *
 * Para un celular argentino eso es `54` + **`9`** + area sin el 0 + numero sin
 * el 15. Sin ese 9 el link abre un chat que no existe, y es el error que hace
 * que "no le llego" sea imposible de diagnosticar.
 *
 * EL 15 ES AMBIGUO Y HAY QUE ADMITIRLO
 * ------------------------------------
 * Los codigos de area argentinos tienen 2, 3 o 4 digitos. Sin una tabla de
 * areas no se puede saber con certeza donde termina el area y empieza el 15, asi
 * que la heuristica busca un "15" en las posiciones donde puede estar y lo saca.
 *
 * Por eso la funcion devuelve TAMBIEN un nivel de confianza y la pantalla
 * muestra el numero antes de abrir el chat. La confirmacion humana es parte del
 * diseño, no un parche: mandarle el remito de un cliente a un desconocido por un
 * digito de mas no se arregla con mas heuristica.
 */
export function normalizarTelefonoAr(
  entrada: string | null | undefined,
): TelefonoNormalizado | null {
  let d = String(entrada ?? "").replace(/\D/g, "");
  if (d === "") return null;

  // Prefijo internacional, con o sin el 00 de discado.
  if (d.startsWith("0054")) d = d.slice(4);
  if (d.startsWith("54")) d = d.slice(2);
  // El 9 de celular, si ya venia puesto. Se vuelve a agregar al final: asi da lo
  // mismo que lo hayan escrito o no.
  if (d.length > 10 && d.startsWith("9")) d = d.slice(1);
  // El 0 del area en discado nacional.
  if (d.startsWith("0")) d = d.slice(1);

  // El 15 vive despues del area, o sea entre la posicion 2 y la 4. Se saca solo
  // si al hacerlo quedan los 10 digitos que corresponden: si no, el "15" era
  // parte del numero y sacarlo lo romperia.
  if (d.length === 12) {
    for (const i of [2, 3, 4]) {
      if (d.slice(i, i + 2) === "15" && d.length - 2 === 10) {
        d = d.slice(0, i) + d.slice(i + 2);
        break;
      }
    }
  }

  if (d === "") return null;

  const confianza = d.length === 10 ? "alta" : "baja";
  const e164 = `549${d}`;

  // Se agrupa solo cuando sabemos que son 10 digitos; si no, se muestra crudo
  // para que se note que algo no cierra.
  const legible =
    confianza === "alta" ? `+54 9 ${d.slice(0, 3)} ${d.slice(3, 6)}-${d.slice(6)}` : `+${e164}`;

  return { e164, legible, confianza };
}

/**
 * El link que abre el chat con el mensaje ya escrito.
 *
 * Con `e164` vacio abre el selector de contactos de WhatsApp: es lo que se usa
 * cuando no hay telefono guardado.
 */
export function linkWhatsapp(e164: string, texto: string): string {
  return `https://wa.me/${e164}?text=${encodeURIComponent(texto)}`;
}
